import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { InputOTP } from '@/components/ui/input-otp'
import { usersService } from '@/services/users'
import { ROUTES } from '@/constants/routes'
import { queryKeys } from '@/lib/query-keys'
import { copyTextToClipboard, downloadTextAsFile } from '@/lib/utils'
import { getApiErrorMessage } from '@/utils/errors'

const TOTP_LENGTH = 6

type Step = 'setup' | 'recovery-codes'

export function Enable2FAPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<Step>('setup')
  const [qrUri, setQrUri] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [setupLoading, setSetupLoading] = useState(true)
  const [setupError, setSetupError] = useState<string | null>(null)
  const [enableError, setEnableError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const setupFetchedRef = useRef(false)
  const submittedCodeRef = useRef('')

  useEffect(() => {
    if (setupFetchedRef.current) return
    setupFetchedRef.current = true
    setSetupLoading(true)
    setSetupError(null)
    usersService
      .get2FASetup()
      .then(data => {
        setQrUri(data.qr_uri)
        setSecret(data.secret)
      })
      .catch(err => {
        setSetupError(getApiErrorMessage(err, t('enable2FA.setupError')))
      })
      .finally(() => {
        setSetupLoading(false)
      })
  }, [t])

  const doEnable = async (code: string) => {
    if (code.length !== TOTP_LENGTH) return
    setEnableError(null)
    setIsSubmitting(true)
    try {
      const data = await usersService.enable2FA(code)
      setRecoveryCodes(data.recovery_codes)
      setStep('recovery-codes')
    } catch (err) {
      setEnableError(getApiErrorMessage(err, t('enable2FA.invalidCode')))
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (
      step === 'setup' &&
      totpCode.length === TOTP_LENGTH &&
      !isSubmitting &&
      submittedCodeRef.current !== totpCode
    ) {
      submittedCodeRef.current = totpCode
      doEnable(totpCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- (doEnable is stable)
  }, [step, totpCode, isSubmitting])

  const handleCopyCodes = () => {
    const text = recoveryCodes.join('\n')
    copyTextToClipboard(text).then(() => {
      toast.success(t('enable2FA.codesCopied'))
    })
  }

  const handleDownloadCodes = () => {
    downloadTextAsFile(recoveryCodes.join('\n'), 'recovery-codes.txt')
  }

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
    navigate(ROUTES.DASHBOARD, { replace: true })
  }

  if (step === 'recovery-codes') {
    return (
      <div className="bg-background px-4 py-8">
        <div className="mx-auto max-w-[605px]">
          <Link
            to={ROUTES.DASHBOARD}
            className="text-foreground hover:text-primary mb-6 inline-flex items-center gap-2 text-sm transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-foreground text-xl font-semibold">
              {t('enable2FA.recoveryCodesTitle')}
            </span>
          </Link>
          <div className="border-border bg-card rounded-2xl border p-6">
            <p className="text-foreground mb-4 text-sm">{t('enable2FA.recoveryCodesWarning')}</p>
            <ul className="border-border bg-muted/30 mb-6 rounded-lg border p-4 font-mono text-sm">
              {recoveryCodes.map((code, i) => (
                <li key={i} className="py-1">
                  {code}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleCopyCodes}
                className="border-button-border bg-card text-foreground hover:bg-button-hover cursor-pointer rounded-lg border px-4 py-2 text-sm font-semibold transition-colors"
              >
                {t('enable2FA.copyCodes')}
              </button>
              <button
                type="button"
                onClick={handleDownloadCodes}
                className="border-button-border bg-card text-foreground hover:bg-button-hover cursor-pointer rounded-lg border px-4 py-2 text-sm font-semibold transition-colors"
              >
                {t('enable2FA.downloadCodes')}
              </button>
              <button
                type="button"
                onClick={handleSaved}
                className="bg-primary text-primary-foreground cursor-pointer rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
              >
                {t('enable2FA.savedButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background px-4 py-8">
      <div className="mx-auto max-w-[605px]">
        <Link
          to={ROUTES.DASHBOARD}
          className="text-foreground hover:text-primary mb-6 inline-flex items-center gap-2 text-sm transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-foreground text-xl font-semibold">{t('enable2FA.title')}</span>
        </Link>
        <div className="border-border bg-card rounded-2xl border p-6">
          {setupLoading ? (
            <div className="bg-placeholder h-48 animate-pulse rounded-lg" />
          ) : setupError ? (
            <p className="text-destructive text-sm">{setupError}</p>
          ) : (
            <form onSubmit={e => e.preventDefault()} className="flex flex-col gap-6">
              {qrUri && (
                <div className="flex justify-center">
                  <QRCodeSVG value={qrUri} size={200} level="M" />
                </div>
              )}
              {secret && (
                <div>
                  <p className="text-label mb-2 text-sm font-semibold">
                    {t('enable2FA.secretLabel')}
                  </p>
                  <p className="border-border bg-muted/30 rounded-lg border p-3 font-mono text-sm break-all">
                    {secret}
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="totp-code" className="mb-2 block text-sm font-semibold text-black">
                  {t('enable2FA.totpLabel')}
                </label>
                <InputOTP
                  id="totp-code"
                  length={TOTP_LENGTH}
                  value={totpCode}
                  onChange={v => setTotpCode(v.replace(/\D/g, ''))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={isSubmitting}
                />
              </div>
              {enableError && (
                <p className="text-destructive text-sm" role="alert">
                  {enableError}
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
